import { CurrencyType } from "@lib/titan2/dist/api/common/controller/net/constant/Constants";
import { BalanceResponseData } from "@lib/titan2/dist/api/common/controller/net/response/data/BalanceResponseData";
import { Balance } from "@lib/titan2/dist/api/common/model/data/Balance";
import { BalanceManager } from "@lib/titan2/dist/api/common/model/data/BalanceManager";
import { ClientData } from "@lib/titan2/dist/api/common/model/data/ClientData";
import { SolidifyableData } from "@lib/titan2/dist/api/core/data/SolidifyableData";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { Action } from "../enum/Action";
import { GameState } from "../enum/GameState";
import { SlingoConstants } from "../enum/SlingoConstants";
import { GameProgressData } from "./GameProgressData";
import { SpinPriceData } from "./SpinPriceData";
import { SpinResultData } from "./SpinResultData";
import { SymbolWinData } from "./SymbolWinData";
/**
 * GameUpdateData.ts
 */
export class GameUpdateData extends SolidifyableData {
    constructor(data) {
        super(data);
        this.solidify();
    }
    hasBonusGameData() {
        return this.hasBonusGame;
    }
    checkForBonusGameInternal(data) {
        let hasBonus = false;
        if (data && this.isObject(data)) {
            for (let key in data) {
                if (!["chanceGame", "slotsGame"].find(prop => key.indexOf(prop) > -1)) {
                    continue;
                }
                let value = data[key];
                if (value !== null) {
                    if (this.isObject(value)) {
                        if (!this.isEmptyObj(value)) {
                            hasBonus = true;
                            break;
                        }
                    }
                    if (Array.isArray(value)) {
                        if (!this.isEmptyArray(value)) {
                            hasBonus = true;
                            break;
                        }
                    }
                }
            }
        }
        return hasBonus;
    }
    isEmptyObj(obj) {
        return Object.keys(obj).length === 0;
    }
    isEmptyArray(arr) {
        return arr.length === 0;
    }
    isObject(variable) {
        return typeof variable === 'object' && variable !== null && !Array.isArray(variable);
    }
    checkForBonusGame() {
        let responseObj = this.data.toObject();
        this.hasBonusGame =
            this.checkForBonusGameInternal(responseObj) ||
                this.checkForBonusGameInternal(responseObj["feature"]) ||
                this.checkForBonusGameInternal(responseObj["spinResult"]) ||
                (responseObj["spinResult"] && this.checkForBonusGameInternal(responseObj["spinResult"]));
    }
    solidify() {
        super.solidify();
        if (this.dataExists(this.data)) {
            this.checkForBonusGame();
            this.gameId = this.data.getNumber(SlingoConstants.PROPERTY_GAME_ID);
            this.gameInstanceId = this.data.getNumber(SlingoConstants.PROPERTY_GAME_INSTANCE_ID);
            this.state = GameState.getState(this.data.getString(SlingoConstants.PROPERTY_STATE));
            this.solidifyActions();
            this.solidifyProgress();
            this.solidifyResult();
            this.solidifySpinPrice();
            this.solidifySymbolWins();
            this.solidifyPatternWin();
            this.solidifyBalance();
            this.solidifyInitialBalance();
            this.solidifyClient();
            this.solidifyFreeGamesRemaining();
            this.solidifyFreeGameStake();
            this.solidifyLastStake();
            this.updateBalanceManager();
        }
    }
    solidifyActions() {
        let actionsData = this.data.getStringArray(SlingoConstants.PROPERTY_ACTIONS);
        this.actions = actionsData.map(key => Action.getAction(key));
    }
    solidifyProgress() {
        let progressData = this.data.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS);
        if (this.dataExists(progressData)) {
            this.progress = new GameProgressData(progressData);
        }
    }
    solidifyResult() {
        let resultData = this.data.getDataObject(SlingoConstants.PROPERTY_SPIN_RESULT);
        if (this.dataExists(resultData)) {
            this.result = new SpinResultData(resultData);
        }
    }
    solidifyClient() {
        let clientData = this.data.getDataObject(SlingoConstants.PROPERTY_CLIENT);
        if (this.dataExists(clientData)) {
            this.client = new ClientData(clientData);
        }
    }
    solidifySpinPrice() {
        let spinPriceData = this.data.getDataObject(SlingoConstants.PROPERTY_SPIN_PRICE);
        if (this.dataExists(spinPriceData)) {
            this.spinPrice = new SpinPriceData(spinPriceData);
        }
    }
    solidifySymbolWins() {
        this.symbolWins = new Dictionary();
        let symbolWins = this.data.getDataArray(SlingoConstants.PROPERTY_SYMBOL_WINS);
        if (symbolWins) {
            for (let i = 0; i < symbolWins.length; i++) {
                let win = new SymbolWinData(symbolWins[i]);
                let type = win.getType();
                if (this.symbolWins.containsKey(type)) {
                    let existingWin = this.symbolWins.getValue(type);
                    existingWin.setAmount(existingWin.getAmount() + win.getAmount());
                }
                else {
                    this.symbolWins.setValue(type, win);
                }
            }
        }
    }
    solidifyPatternWin() {
        this.patternWin = this.data.getDataObject(SlingoConstants.PROPERTY_GAME_PROGRESS)?.getNumber(SlingoConstants.PROPERTY_PATTERN_WIN);
    }
    solidifyBalance() {
        const balanceData = new BalanceResponseData(this.data);
        balanceData.solidify();
        const balanceManager = BalanceManager.getInstance();
        this.balance = new Balance(balanceManager.getTotalBalance(), balanceManager.getCurrency(), CurrencyType.CASH, balanceManager.getCashBalance(), balanceManager.getBonusBalance());
    }
    solidifyInitialBalance() {
        const client = this.data.getDataObject(SlingoConstants.PROPERTY_CLIENT);
        const initialBalanceResponse = client?.getDataObject(SlingoConstants.PROPERTY_INITIAL_BALANCE);
        const initialBalance = initialBalanceResponse?.toObject();
        if (initialBalance) {
            let totalAmount = 0;
            let cashAmount = 0;
            let bonusAmount = 0;
            for (let i = 0; i < initialBalance.length; i++) {
                const balance = initialBalance[i];
                totalAmount += balance.amount;
                if (balance.type === CurrencyType.CASH) {
                    cashAmount += balance.amount;
                }
                else if (balance.type === CurrencyType.BONUS) {
                    bonusAmount += balance.amount;
                }
                else {
                    console.warn('Unknown balance type in initial balance response: ', balance.type);
                }
            }
            const balanceManager = BalanceManager.getInstance();
            this.initialBalance = new Balance(totalAmount, balanceManager.getCurrency(), CurrencyType.CASH, cashAmount, bonusAmount);
        }
    }
    updateBalanceManager() {
        if (this.state?.isComplete()) {
            let patternWin = this.progress.getPatternWin();
            let symbolWin = this.progress.getSymbolWin();
            let totalWin = symbolWin + patternWin;
            BalanceManager.getInstance().setTotalWin(totalWin);
        }
    }
    solidifyFreeGamesRemaining() {
        this.freeGamesRemaining = this.getSafeNumber(SlingoConstants.PROPERTY_FREE_GAMES_REMAINING);
    }
    solidifyFreeGameStake() {
        this.freeGameStake = this.getSafeNumber(SlingoConstants.PROPERTY_FREE_GAME_STAKE);
    }
    solidifyLastStake() {
        this.lastStake = this.getSafeNumber(SlingoConstants.PROPERTY_LAST_STAKE);
    }
    getActions() {
        return this.actions.slice();
    }
    getGameId() {
        return this.gameId;
    }
    getGameInstanceId() {
        return this.gameInstanceId;
    }
    getState() {
        return this.state;
    }
    getGameProgress() {
        return this.progress;
    }
    getClient() {
        return this.client;
    }
    getSpinResult() {
        return this.result;
    }
    getSpinPrice() {
        return this.spinPrice;
    }
    getPatternWin() {
        return this.patternWin;
    }
    getSymbolWins() {
        return this.symbolWins;
    }
    getBalance() {
        return this.balance;
    }
    getInitialBalance() {
        return this.initialBalance;
    }
    getFreeGameStake() {
        return this.freeGameStake;
    }
    getFreeGamesRemaining() {
        return this.freeGamesRemaining;
    }
    hasBalance() {
        return this.balance != null;
    }
    hasInitialBalance() {
        return this.initialBalance != null;
    }
    hasClient() {
        return this.client != null;
    }
    hasFreeGame() {
        return this.freeGamesRemaining > 0;
    }
    hasLastStake() {
        return this.lastStake > 0;
    }
    getLastStake() {
        return this.lastStake;
    }
}
//# sourceMappingURL=GameUpdateData.js.map